import React, { ReactElement } from 'react';
import { ScriptStore, BookingStore, RootStore } from 'store';
import { formatToFullDate, getServiceFormat } from 'helpers';
import { CallbackSource, JsonDataItem } from 'types';
import { format } from 'date-fns';
import { isArray, isEmpty } from 'lodash';

export const useText = () => {
  type ConditionKeys =
    | 'productId'
    | 'isCustomerPrepaid'
    | 'isProductPrepaid'
    | 'jobServiceType'
    | 'serviceAvailabilityType'
    | 'serviceFormat'
    | 'isCallback'
    | 'device'
    | 'isCustomerInvoice'
    | 'isOw30dba'
    | 'isCustomerNew'
    | 'paymentMethod'
    | 'hasExpiredCard';
  const config = ScriptStore.script;

  const { user } = RootStore;
  const {
    customer,
    productSelection,
    productList,
    servicePrice,
    paymentInfo,
    technician,
    slot,
    appointment,
    callback,
    serviceType,
    isNewCustomer,
    paymentMethod,
    lostJobReasons,
  } = BookingStore;

  const variables: Record<string, string> = {
    userName: user?.name?.split(' ')[0] || '',
    customerName: customer?.name?.contact1_firstname || lostJobReasons?.customer?.contact1_firstname || '[***]',
    customerEmail: customer?.email || lostJobReasons?.customer?.email || '[***]',
    minServiceFee: servicePrice?.minServiceFee || '',
    todayPrice: servicePrice?.todayPrice || '',
    sameDayFee: servicePrice?.sameDayFee || '',
    halfHourFee: servicePrice?.halfHourFee || '',
    tomorrowPrice: servicePrice?.tomorrowPrice || '',
    weekendPrice: servicePrice?.weekendPrice || '',
    weekendFee: servicePrice?.weekendFee || '',
    appointmentDate: slot ? formatToFullDate(slot.day) : '',
    appointmentStartTime: appointment ? format(new Date(appointment.appointment_start), 'h:mmaaa') : '',
    appointmentEndTime: appointment ? format(new Date(appointment.appointment_end), 'h:mmaaa') : '',
    technicianName: technician?.name.split(' ')[0] || '',
    cardLastDigits: paymentInfo?.card_details?.card_number.slice(-3) || '',
    cardholderName: paymentInfo?.card_details?.cardholder || '',
    cardName: paymentInfo?.card_details?.card_name || '',
  };

  const conditions: Record<ConditionKeys, any> = {
    isCustomerNew: Boolean(isNewCustomer || (lostJobReasons && isEmpty(lostJobReasons?.customer))).toString(),
    isCustomerPrepaid: Boolean(customer?.prepaid).toString(),
    isCustomerInvoice: Boolean(customer?.invoiced).toString(),
    isCallback: Boolean(callback).toString(),
    isOw30dba: Boolean(customer?.ow30dba).toString(),
    jobServiceType: productSelection?.serviceType,
    device: productSelection?.device,
    productId: productSelection?.products?.map((product: JsonDataItem) => product.id),
    isProductPrepaid: (
      Boolean(callback?.source === CallbackSource.OWSale) ||
      productSelection?.products?.some(({ id }: { id: string }) =>
        Boolean(productList?.find((p) => p.ID.toString() === id)?.prepaid),
      )
    )?.toString(),
    serviceAvailabilityType: getServiceFormat(productSelection?.products, productList),
    serviceFormat: serviceType,
    paymentMethod: paymentMethod,
    hasExpiredCard: Boolean(!paymentInfo?.card_details?.is_card_valid).toString(),
  };

  const getText = (key: string): ReactElement<any> | string => {
    const configEntry = config[key];
    if (!configEntry) return <></>;

    const matchingVariant = configEntry.variants?.find((variant: JsonDataItem) =>
      Object.entries(variant.condition || {}).every(([key, value]) =>
        isArray(conditions[key as ConditionKeys])
          ? conditions[key as ConditionKeys]?.includes(value)
          : conditions[key as ConditionKeys] === value,
      ),
    );

    const rawText = matchingVariant ? matchingVariant.text : configEntry.defaultText;
    const parsedScript = rawText?.replace(/{(.*?)}/g, (_: unknown, key: string) => variables[key] || '') || '';

    return parsedScript === '' ? parsedScript : <span dangerouslySetInnerHTML={{ __html: parsedScript }} />;
  };

  return { getText };
};
